/**
 * Whether a معلم is actually there.
 *
 * An open session used to stay in the discovery list whether or not anyone was
 * watching it: a معلم who switched to the طالب half, opened the admin portal,
 * or simply put the phone down left a session advertising itself, and students
 * queued into a room nobody was attending.
 *
 * Namat has the same hole and does not close it — a coach's clinic stays open
 * until they toggle it — so this is not a port. Closing the session was the
 * other option and is the wrong lever: it clears the room and drops the whole
 * queue, so a glance at another screen would destroy the people waiting. What
 * is actually wrong is VISIBILITY, so that is what this changes. The session
 * survives, invisible, and comes back the moment the معلم does.
 */

/** How often the معلم's screen says it is still there. */
export const MUQRI_BEAT_MS = 10000;

/**
 * How long a session stays listed after the last beat.
 *
 * Deliberately far longer than the students' 15 s: a student dropping out of a
 * queue costs them their place, while a session vanishing costs every student
 * looking for one. Long enough to ride out a screen change or a moment of bad
 * signal, short enough that nobody joins a session that was abandoned a minute
 * ago.
 */
export const MUQRI_ABSENT_MS = 45000;

export type Attendance = {
  /** Millis of the last beat, or 0 when the session has never reported one. */
  muqriSeenAt?: number;
  /** A live call means attended, whatever the beats say. */
  hasActiveCall?: boolean;
};

/**
 * A session in a CALL is attended by definition — the معلم is on the call
 * screen, not the clinic screen, so no beat is arriving. Judging that session
 * by its beats alone would hide a session from discovery while a recitation
 * was going on inside it.
 *
 * A session that has never reported a beat is treated as attended too: it was
 * opened by a client that does not send them, and hiding every session from
 * an older build would be worse than listing one that is idle.
 */
export function isAttended(session: Attendance, now: number): boolean {
  if (session.hasActiveCall) return true;
  const seen = session.muqriSeenAt ?? 0;
  if (!seen) return true;
  return now - seen <= MUQRI_ABSENT_MS;
}
